import React, { FC, useEffect, useState } from 'react';
import * as S from './styles';
import { FormikValues, useFormik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { Radio, type RadioChangeEvent, Select, type SelectProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { cardSelect } from '@entities/card';
import { AppDispatch } from '@config/store';
import { getCardForms, getCardTemplates } from '@api/thunks/card';
import { authInfoSelect } from '@entities/auth';
import { validationSchema } from './validationSchema';
import { ICreateRegForm } from '@api/services/cardApi/types';
import { createRegFormRequest } from '@api/services/cardApi';
import { checkIsResponseStatus } from '@utils/functions';

interface CreateFormProps {
    modalVisibilityToggler: () => void;
}

const initialValues = {
    title: '',
    subTitle: '',
    name: '',
    startDate: '',
    endDate: '',
    template_id: '',
    issueCards: 0,
    state: 1,
    clientState: 1,
    cardState: 1,
};

export const CreateFormModal: FC<CreateFormProps> = ({
    modalVisibilityToggler,
}) => {
    const { templates } = useSelector(cardSelect);
    const { shop } = useSelector(authInfoSelect);
    const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(
        null
    );

    const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null);
    const [additionalSettings, setAdditionalSettings] =
        useState<boolean>(false);
    const dispatch = useDispatch<AppDispatch>();

    const onSubmit = async ({
        issueCards,
        endDate,
        startDate,
        name: formName,
        title,
        template_id,
        subTitle,
        state,
        clientState,
        cardState,
    }: FormikValues) => {
        if (!shop) return;
        const data: ICreateRegForm = {
            endDate: !endDate ? null : endDate,
            startDate: !startDate ? null : startDate,
            name: formName,
            textAfter: subTitle,
            textBefore: title,
            template_id: +template_id,
            issueCards: issueCards === 0 ? null : issueCards,
            state,
            clientState,
            cardState,
        };

        await createRegFormRequest(shop.id, data).then(res => {
            if (checkIsResponseStatus(res.status)) {
                dispatch(getCardForms(shop.id));
            }
            modalVisibilityToggler();
        });
    };
    const {
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        values,
        handleBlur,
        handleChange: handleFormicChange,
    } = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    const options: SelectProps['options'] = [
        ...(templates?.map(item => ({
            label: item.name,
            value: item.id.toString(),
        })) || []),
    ];

    const handleChange = (values: string) => {
        setFieldValue('template_id', values);
    };

    const handleDateChange = (date: unknown) => {
        if (dayjs.isDayjs(date)) {
            setSelectedStartDate(date);
            setFieldValue('startDate', date.format());
        } else {
            setSelectedStartDate(null);
            setFieldValue('startDate', '');
        }
    };
    const handleStateChange = (e: RadioChangeEvent) => {
        console.log('handleStateChange', e);
        setFieldValue('state', e.target.value);
    };

    const handleClientChange = (e: RadioChangeEvent) => {
        setFieldValue('clientState', e.target.value);
    };

    const handleCardChange = (e: RadioChangeEvent) => {
        setFieldValue('cardState', e.target.value);
    };

    const handleEndDateChange = (date: unknown) => {
        if (dayjs.isDayjs(date)) {
            setSelectedEndDate(date);
            setFieldValue('endDate', date.format());
        } else {
            setSelectedStartDate(null);
            setFieldValue('endDate', '');
        }
    };

    useEffect(() => {
        if (shop) {
            dispatch(getCardTemplates(shop.id));
        }
    }, [dispatch, shop]);

    return (
        <S.Wrapper>
            <S.CloseBtn onClick={modalVisibilityToggler}>Закрыть</S.CloseBtn>
            <S.FormContainer
                onSubmit={event => {
                    handleSubmit(event);
                }}
            >
                <S.FormItemContainer>
                    <S.Label>Название формы</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.name && errors.name ? 'error' : ''
                        }
                        help={touched.name && errors.name ? errors.name : ''}
                    >
                        <S.Input
                            type="text"
                            name="name"
                            value={values.name}
                            required={!!errors.name && !!touched.name}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!(errors.name && touched.name)}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.Label>Заголовок формы</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.title && errors.title ? 'error' : ''
                        }
                        help={touched.title && errors.title ? errors.title : ''}
                    >
                        <S.Input
                            type="text"
                            name="title"
                            value={values.title}
                            required={!!errors.title && !!touched.title}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!(errors.title && touched.title)}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.Label>Подзаголовок формы</S.Label>
                    <S.FormItem
                        validateStatus={
                            touched.subTitle && errors.subTitle ? 'error' : ''
                        }
                        help={
                            touched.subTitle && errors.title
                                ? errors.subTitle
                                : ''
                        }
                    >
                        <S.Input
                            type="text"
                            name="subTitle"
                            value={values.subTitle}
                            required={!!errors.subTitle && !!touched.subTitle}
                            onChange={handleFormicChange}
                            onBlur={handleBlur}
                            $hasError={!!(errors.subTitle && touched.subTitle)}
                        />
                    </S.FormItem>
                </S.FormItemContainer>
                <S.FormItemContainer>
                    <S.FormItem
                        validateStatus={
                            touched.template_id && errors.template_id
                                ? 'error'
                                : ''
                        }
                        help={
                            touched.template_id && errors.template_id
                                ? errors.template_id
                                : ''
                        }
                    >
                        <div>Выбрать макеты</div>
                        <S.WrapSelect>
                            <Select
                                allowClear
                                style={{
                                    width: '100%',
                                    zIndex: 500,
                                    height: '100%',
                                }}
                                placeholder="Выбрать макеты"
                                value={values.template_id}
                                onChange={handleChange}
                                options={options}
                            />
                        </S.WrapSelect>
                    </S.FormItem>
                </S.FormItemContainer>
                {additionalSettings && (
                    <>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <S.Label>Дата начала действия</S.Label>
                                <S.StyledDatePicker
                                    variant="outlined"
                                    format="DD-MM-YYYY"
                                    placeholder="DD-MM-YYYY"
                                    value={selectedStartDate}
                                    onChange={handleDateChange}
                                    getPopupContainer={() => document.body}
                                    inputReadOnly={true}
                                    style={{
                                        width: '100%',
                                        height: '41px',
                                        fontSize: '12px',
                                        marginBottom: '8px',
                                    }}
                                />
                            </S.FormItem>
                        </S.FormItemContainer>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <S.Label>Дата окончания действия</S.Label>
                                <S.StyledDatePicker
                                    variant="outlined"
                                    format="DD-MM-YYYY"
                                    placeholder="DD-MM-YYYY"
                                    value={selectedEndDate}
                                    onChange={handleEndDateChange}
                                    getPopupContainer={trigger =>
                                        trigger.parentElement!
                                    }
                                    inputReadOnly={true}
                                    style={{
                                        width: '100%',
                                        height: '41px',
                                        fontSize: '12px',
                                        marginBottom: '8px',
                                    }}
                                />
                            </S.FormItem>
                        </S.FormItemContainer>

                        <S.FormItemContainer>
                            <S.Label>Лимит карт</S.Label>
                            <S.FormItem>
                                <S.Input
                                    type="number"
                                    name="issueCards"
                                    value={values.issueCards}
                                    required={!!errors.issueCards}
                                    onChange={handleFormicChange}
                                    onBlur={handleBlur}
                                    $hasError={!!errors.issueCards}
                                />
                            </S.FormItem>
                        </S.FormItemContainer>
                        <S.FormItemContainer>
                            <span>Форма активна:</span>
                            <S.FormItem>
                                <Radio.Group
                                    onChange={handleStateChange}
                                    value={values.state}
                                >
                                    <Radio value={1}>Активна</Radio>
                                    <Radio value={0}>Не активна</Radio>
                                </Radio.Group>
                            </S.FormItem>
                        </S.FormItemContainer>
                        <S.FormItemContainer>
                            <span>Клиент активен после регистрации:</span>
                            <S.FormItem>
                                <Radio.Group
                                    onChange={handleClientChange}
                                    value={values.clientState}
                                >
                                    <Radio value={1}>Активен</Radio>
                                    <Radio value={0}>Блокирован</Radio>
                                </Radio.Group>
                            </S.FormItem>
                        </S.FormItemContainer>
                        <S.FormItemContainer>
                            <span>Карта активна после регистрации:</span>
                            <S.FormItem>
                                <Radio.Group
                                    onChange={handleCardChange}
                                    value={values.cardState}
                                >
                                    <Radio value={1}>Активна</Radio>
                                    <Radio value={0}>Блокирована</Radio>
                                </Radio.Group>
                            </S.FormItem>
                        </S.FormItemContainer>
                    </>
                )}
                <S.StyledBtn
                    type="button"
                    onClick={() => setAdditionalSettings(!additionalSettings)}
                >
                    {!additionalSettings
                        ? 'Показать дополнительные параметры'
                        : 'Скрыть дополнительные параметры'}
                </S.StyledBtn>
                <S.StyledButtonSend type="submit">
                    <span>Создать</span>
                </S.StyledButtonSend>
            </S.FormContainer>
        </S.Wrapper>
    );
};
